import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import UserProfile from './user-profile/UserProfile';
import {Typography} from '@mui/material';
import {useEffect, useState, useMemo, useRef} from 'react';
import {ISelectItem} from '../../../shared/types';
import {ReactComponent as NotificationWithDot} from '../../../assets/images/notification-with-red-dot.svg';
import {ReactComponent as NotificationOutline} from '../../../assets/images/notificationOutline.svg';
import {ReactComponent as OpenBook} from '../../../assets/images/open-book.svg';
import {t} from 'i18next';
import './HeaderBar.scss';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrTooltip from '../../common/arr-tooltip/ArrTooltip';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useQueryClient} from '@tanstack/react-query';
import {RootState} from '../../../redux/store';
import {
  setShowRoleImpersonationModal,
  setRoleImpersonationPrefill,
  clearRoleImpersonationPrefill,
} from '../../../redux/slice/roleImpersonationSlice';
import {
  useDomains,
  useSecurityGroupsRoleImpersonation,
  useChangeUserRole,
} from '../../../hooks/users/useUsers';
import debounce from 'lodash/debounce';
import {
  useGetNotifications,
  useGetNotificationsCount,
  usePostNotificationCount,
} from '../../../hooks/notifications/useNotifications';
import NotificationsMenu from '../../notifications/NotificationsMenu';
import {NotificationResponse} from '../../../api/types/notifications.type';
import {clearSignalREvents} from '../../../redux/slice/signalrSlice';
import {setAuthenticatedUser} from '../../../redux/slice/authenticatedUserSlice';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import {useAllProjectsData} from '../../../hooks/dashboard/useDashboard';

import {logoutSSO} from '../../../api/endpoints/authenticationApi';
import {SSO_ENABLED} from '../../../config/env';
import {getSecurityGroupsByRoleAndProject} from '../../../api/endpoints/usersApi';
import {RoleId} from '../../../shared/constants';
import {clearLocalStorage} from '../../../utils/utils';

const auroraCentralUrl = 'https://www.sightlinecentral.com/';

const roles = {
  systemAdmin: 'System Administrator',
  domainAdmin: 'Domain Administrator',
  reviewManager: 'Review Manager',
  reviewer: 'Reviewer',
  projectAdmin: 'Project Administrator',
};

const SIGNALR_EVENT = 'ProcessingSyncSuccessNotification';
const HeaderBar = () => {
  const dispatch = useDispatch();
  const SSO_Flag = Boolean(SSO_ENABLED === 'true' ? true : false);
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);

  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<ISelectItem | null>(
    null
  );
  const [selectedDomain, setSelectedDomain] = useState<ISelectItem | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [notificationsData, setNotificationsData] = useState<
    NotificationResponse[] | null
  >([]);
  const [isNewNotifications, setIsNewNotifications] = useState<boolean>(false);

  const navigate = useNavigate();

  const {mutate: fetchNotifications} = useGetNotifications();
  const {mutate: postNotificationCount} = usePostNotificationCount();
  const {data: notificationCountData, refetch: refetchNotificationCount} =
    useGetNotificationsCount();
  const {data: headerData, refetch} = useAllProjectsData(
    loginUserData,
    !!loginUserData
  );

  const [selectedSecurityGroup, setSelectedSecurityGroup] =
    useState<ISelectItem | null>(null);
  const [showArrCentralModal, setShowArrCentralModal] = useState(false);

  const notificationButtonRef = useRef<HTMLButtonElement | null>(null);
  const {data: fetchedDomains = []} = useDomains(
    loginUserData?.roleId,
    loginUserData.entityId,
    {enabled: loginUserData?.roleName === roles.domainAdmin}
  );

  useEffect(() => {
    refetchNotificationCount();
    dispatch(clearSignalREvents());
    setIsNewNotifications(false);
  }, [roleId]);

  useEffect(() => {
    if (
      lastEvent?.payload.type === 'NotificationCount' ||
      (notificationCountData ?? 0) > 0
    ) {
      setIsNewNotifications(true);
    }
  }, [lastEvent, notificationCountData]);

  useEffect(() => {
    const {payload} = lastEvent || {};
    if (!payload) return;
    // only refetch when the event type is ProcessingSyncSuccessNotification AND the event's project id matches the selected project
    if (
      payload?.type === SIGNALR_EVENT &&
      selectedProject &&
      String(selectedProject.id) === String(payload?.payload?.projectId)
    ) {
      refetch();
    }
  }, [lastEvent]);

  useEffect(() => {
    const {payload} = lastEvent || {};
    if (!payload) return;
    if (
      payload.type === 'ImportSetNotification' &&
      loginUserData?.roleName === roles.projectAdmin &&
      selectedProject &&
      String(selectedProject.id) === String(payload?.payload?.projectId)
    ) {
      setIsNewNotifications(true);
    }
  }, [lastEvent, loginUserData, selectedProject]);

  const isPojectSelected = !!selectedProject;
  const {data: availableSecurityGroups = []} =
    useSecurityGroupsRoleImpersonation(
      loginUserData?.roleId,
      Number(selectedProject?.id),
      {
        enabled:
          (loginUserData?.roleName === roles.reviewManager ||
            loginUserData?.roleName === roles.reviewer) &&
          isPojectSelected,
      }
    );

  // Mutations
  const {mutate: changeUserRole, isPending: isChangeUserRolePending} =
    useChangeUserRole();

  const domainOptions = fetchedDomains.map((d) => ({id: d.key, name: d.value}));
  const projectOptions = (headerData?.lstProject || []).map((p) => ({
    id: p.key,
    name: p.value,
  }));
  const securityGroupOptions = useMemo<ISelectItem[]>(() => {
    return availableSecurityGroups?.map((s) => ({
      id: s.key,
      name: s.value,
    }));
  }, [availableSecurityGroups]);

  // Guard: Ensure selectedSecurityGroup is always in options or null
  useEffect(() => {
    if (
      selectedSecurityGroup &&
      !securityGroupOptions.some((s) => s.id === selectedSecurityGroup.id)
    ) {
      setSelectedSecurityGroup(null);
    }
  }, [securityGroupOptions, selectedSecurityGroup]);

  // Debounced prefill dispatcher to avoid flooding the store on rapid changes
  const debouncedSetPrefill = useMemo(
    () =>
      debounce(
        (
          payload: Partial<{
            domain: ISelectItem | null;
            project: ISelectItem | null;
            securityGroup: ISelectItem | null;
          }>
        ) => dispatch(setRoleImpersonationPrefill(payload)),
        200
      ),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      debouncedSetPrefill.cancel();
    };
  }, [debouncedSetPrefill]);

  useEffect(() => {
    if (loginUserData.entityId) {
      const match = domainOptions.find(
        (d) => String(d.id) === String(loginUserData.entityId)
      );
      if (match && selectedDomain?.id !== match.id) {
        setSelectedDomain(match);
        dispatch(setRoleImpersonationPrefill({domain: match}));
      }
    }
  }, [loginUserData, domainOptions]);

  useEffect(() => {
    const showProject =
      loginUserData?.roleName !== roles.systemAdmin &&
      loginUserData?.roleName !== roles.domainAdmin;
    if (!showProject) return;
    if (!loginUserData.projectId) return;
    const match = projectOptions.find(
      (p) => String(p.id) === String(loginUserData.projectId)
    );
    if (match && selectedProject?.id !== match.id) {
      setSelectedProject(match);
      dispatch(
        setRoleImpersonationPrefill({
          project: match ? {id: match.id, name: match.name} : null,
        })
      );
    }
  }, [projectOptions, selectedProject, loginUserData]);

  useEffect(() => {
    if (loginUserData && securityGroupOptions.length > 0) {
      const match =
        loginUserData.securityGroupId &&
        securityGroupOptions.find(
          (s) => s.id === loginUserData.securityGroupId
        );
      if (match) {
        setSelectedSecurityGroup(match);
      } else {
        setSelectedSecurityGroup(securityGroupOptions[0]);
      }
    }
  }, [loginUserData, securityGroupOptions]);

  const handleExploreNowClick = () => {
    window.open(auroraCentralUrl, '_blank', 'noopener,noreferrer');
  };
  const changeRoleHandler = () => {
    dispatch(setShowRoleImpersonationModal(true));
  };

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications(undefined, {
      onSuccess: (data: NotificationResponse[]) => {
        setNotificationsData(data || []);
        postNotificationCount({});
        setIsNewNotifications(false);
        dispatch(clearSignalREvents());
      },
    });
  };

  const handleClose = (restoreFocus = true) => {
    setAnchorEl(null);
    if (restoreFocus) {
      requestAnimationFrame(() => {
        notificationButtonRef.current?.focus();
      });
    }
  };

  /**
   * Handler for domain selection change (Domain Admin)
   */
  const handleDomainChange = (domain: ISelectItem | null) => {
    if (!domain) return;
    if (selectedDomain && selectedDomain.id === domain.id) return;

    changeUserRole(
      {
        role: Number(loginUserData?.roleId ?? -1),
        entityID: Number(domain?.id ?? -1),
      },
      {
        onSuccess: (data) => {
          const targetPath = data.landingPage;
          navigate(targetPath);
          dispatch(
            setAuthenticatedUser({
              ...loginUserData,
              entityId: domain.id as number,
            })
          );
          debouncedSetPrefill({
            domain,
            project: null,
            securityGroup: null,
          });
        },
        onError: (err) => {
          console.error('Failed to change project context', err);
        },
      }
    );
  };

  /**
   * Handler for project selection change (Project Admin, Review Manager, Reviewer)
   */
  const handleProjectChange = async (project: ISelectItem | null) => {
    if (!project || !project.id) return;
    if (selectedProject && selectedProject.id === project.id) return;
    let securityGroupId = 1;
    try {
      if (loginUserData?.roleId >= Number(RoleId.ReviewManager)) {
        const groups = await getSecurityGroupsByRoleAndProject(
          loginUserData?.roleId,
          Number(project.id)
        );
        securityGroupId = groups[0]?.key ?? 1;
      }

      changeUserRole(
        {
          role: Number(loginUserData?.roleId ?? -1),
          entityID: Number(loginUserData?.entityId ?? -1),
          projectID: Number(project.id),
          securityGroupID: securityGroupId,
        },
        {
          onSuccess: (data: any) => {
            const targetPath = data.landingPage;
            navigate(targetPath);
            const mergedUser = {
              ...loginUserData,
              ...data,
              projectName: project.name,
            };

            dispatch(setAuthenticatedUser(mergedUser));
            debouncedSetPrefill({
              domain: data.entityId ? {id: data.entityId, name: ''} : null,
              securityGroup: data.securityGroupId
                ? {id: data.securityGroupId, name: ''}
                : null,
              project,
            });
          },
          onError: (err) => {
            console.error('Failed to change project context', err);
          },
        }
      );
    } catch (err) {
      console.error('Failed to change project context', err);
    }
  };

  /**
   * Handler for security group selection change (Review Manager, Reviewer)
   */
  const handleSecurityGroupChange = (securityGroup: ISelectItem | null) => {
    if (!securityGroup) {
      return;
    }
    if (selectedSecurityGroup && selectedSecurityGroup.id === securityGroup.id)
      return;

    const valid = securityGroupOptions?.some((s) => s.id === securityGroup.id);
    if (!valid) return;

    changeUserRole(
      {
        role: Number(loginUserData?.roleId ?? -1),
        entityID: Number(loginUserData?.entityId ?? -1),
        projectID: Number(loginUserData?.projectId ?? -1),
        securityGroupID: Number(securityGroup.id),
      },
      {
        onSuccess: (data: any) => {
          const targetPath = data.landingPage;
          navigate(targetPath);
          const mergedUser = {
            ...loginUserData,
            ...data,
          };

          dispatch(setAuthenticatedUser(mergedUser));
          debouncedSetPrefill({
            securityGroup,
          });
        },
        onError: (err) => {
          console.error('Failed to change security group context', err);
        },
      }
    );
  };

  const SSOLogout = async () => {
    await logoutSSO();
    clearLocalStorage();
    sessionStorage.removeItem('presistence-search');
    dispatch({type: 'auth/logout'});
    dispatch(clearRoleImpersonationPrefill());
    queryClient.clear();
    navigate('/login');
  };
  return (
    <>
      <Grid
        component="header"
        container
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        className={`aur-header ${loginUserData?.roleName === roles.systemAdmin ? 'aur-header-without-dropdown' : ''}`}
        data-testid="scroll-container"
      >
        {loginUserData?.roleName === roles.domainAdmin && (
          <Box className={'dropdowns'}>
            <Typography
              className="typo-text body3_size project-text"
              data-testid={`header-bar-domain`}
            >
              {t('arrTopNav.domain')}
            </Typography>
            <ArrSingleSelectDropdown
              dropdownLabel={t('arrTopNav.domainName')}
              dropdownItems={domainOptions}
              selectedValues={selectedDomain}
              onSelectionChange={handleDomainChange}
              searchable
              sizeVariant="medium"
              customWidth={15}
              showNoResultsText
              dataTestId="asd-header-bar-domainName"
              selectRestProps={{'aria-label': t('arrTopNav.selectADomain')}}
            />
          </Box>
        )}
        {loginUserData?.roleName !== roles.systemAdmin &&
          loginUserData?.roleName !== roles.domainAdmin && (
            <Box className={'dropdowns'}>
              <Typography
                className="typo-text body3_size project-text"
                data-testid={`header-bar-project`}
              >
                {t('arrTopNav.project')}
              </Typography>
              <ArrSingleSelectDropdown
                dropdownLabel={t('arrTopNav.projectName')}
                dropdownItems={projectOptions}
                selectedValues={selectedProject}
                onSelectionChange={handleProjectChange}
                searchable
                sizeVariant="medium"
                customWidth={15}
                showNoResultsText
                dataTestId="asd-header-bar-projectName"
                selectRestProps={{'aria-label': t('arrTopNav.selectAProject')}}
              />
              {(loginUserData?.roleName === roles.reviewManager ||
                loginUserData?.roleName === roles.reviewer) && (
                <>
                  <Typography
                    className="typo-text body3_size security-group-text"
                    data-testid={`header-bar-security-group`}
                  >
                    {t('arrTopNav.securityGroup')}
                  </Typography>
                  <ArrSingleSelectDropdown
                    dropdownLabel={t('arrTopNav.defaultSecurityGroup')}
                    dropdownItems={securityGroupOptions}
                    selectedValues={selectedSecurityGroup}
                    onSelectionChange={handleSecurityGroupChange}
                    searchable
                    sizeVariant="medium"
                    customWidth={15}
                    tabIndex={-1}
                    showNoResultsText
                    dataTestId="asd-header-bar-defaultSecurityGroup"
                    selectRestProps={{
                      'aria-label': t('arrTopNav.selectASecurityGroup'),
                    }}
                  />
                </>
              )}
            </Box>
          )}
        <div className="right-items-div" data-testid={`header-bar-div`}>
          {headerData?.analyticsIndicatorStatus &&
            headerData?.analyticsIndicatorStatus !==
              'AnalyticsNoActionRequire' && (
              <ArrTooltip
                labelId="analytics-indicator-status"
                content={
                  headerData?.analyticsIndicatorStatus === 'AnalyticsInProgress'
                    ? t('arrTopNav.analyticsInProgressDesc')
                    : t('arrTopNav.analyticsInCompleteDesc')
                }
                placement="bottom"
                trigger="click"
                focusableWrapper
              >
                <Box
                  className="analytics-alert-btn"
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.currentTarget.click();
                    }
                  }}
                  aria-label={
                    headerData?.analyticsIndicatorStatus ===
                    'AnalyticsInProgress'
                      ? t('arrTopNav.analyticsInProgress')
                      : t('arrTopNav.analyticsInComplete')
                  }
                >
                  <WarningOutlinedIcon />
                  <Typography
                    className="typo-text label2_size custom-text"
                    data-testid={`header-bar-analytics-indicator`}
                  >
                    {headerData?.analyticsIndicatorStatus ===
                    'AnalyticsInProgress'
                      ? t('arrTopNav.analyticsInProgress')
                      : t('arrTopNav.analyticsInComplete')}
                  </Typography>
                </Box>
              </ArrTooltip>
            )}
          {headerData?.isReIndexRequired && (
            <ArrTooltip
              labelId="reindex-required-status"
              data-testid={`header-bar-reindex-tooltip`}
              content={t('arrTopNav.projectReindexRequired')}
              placement="bottom"
              trigger="click"
              focusableWrapper
            >
              <Box
                className="analytics-alert-btn"
                tabIndex={0}
                role="button"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.currentTarget.click();
                  }
                }}
                aria-label={t('arrTopNav.reIndexRequired')}
              >
                <WarningOutlinedIcon />
                <Typography
                  className="typo-text label2_size custom-text"
                  data-testid={`header-bar-reindex-indicator`}
                >
                  {t('arrTopNav.reIndexRequired')}
                </Typography>
              </Box>
            </ArrTooltip>
          )}
          <Box className="header-icons">
            <ArrTooltip
              content={t('arrTopNav.arrCentralTooltipContent')}
              placement="bottom"
              offset={10}
              tabIndex={-1}
              labelId="analytics-central-icn"
            >
              <ArrIconButton
                icon={<OpenBook />}
                onClick={() => setShowArrCentralModal(true)}
                ariaLabel="aurora-review-central"
                shape="default"
                size="medium"
                className="open-book-icon-btn"
                testId="header-bar-aurora-review-central-icon-button"
              />
            </ArrTooltip>
            <ArrTooltip
              tabIndex={-1}
              content={t('arrTopNav.arrNotificationTooltipContent')}
              placement="bottom"
              labelId="analytics-notification-status"
              offset={10}
            >
              <ArrIconButton
                id="notifications-button"
                ref={notificationButtonRef}
                icon={
                  isNewNotifications ? (
                    <NotificationWithDot focusable="false" aria-hidden="true" />
                  ) : (
                    <NotificationOutline focusable="false" aria-hidden="true" />
                  )
                }
                onClick={handleClick}
                ariaLabel="notification"
                aria-haspopup="menu"
                aria-controls={open ? 'notifications-menu' : undefined}
                shape="default"
                size="medium"
                className="notification-icon-btn"
                aria-expanded={open ? 'true' : 'false'}
                testId="header-bar-notification-icon-button"
              />
            </ArrTooltip>
          </Box>
          <UserProfile
            user={{
              userName: loginUserData?.userName || '',
              role: loginUserData?.roleName || '',
            }}
            menuItems={[
              {
                id: '1',
                label: (
                  <>
                    <Box>
                      <Typography
                        className="typo-text body3_size"
                        data-testid={`header-bar-change-role`}
                      >
                        {t('arrTopNav.menuItem.changeRole')}
                      </Typography>
                    </Box>
                    <Box className="role-div">
                      <Typography
                        className="typo-text label3_size role-text"
                        data-testid={`header-bar-typography-text`}
                      >
                        {loginUserData?.roleName}
                      </Typography>
                    </Box>
                  </>
                ),
                onClick: changeRoleHandler,
              },
              {
                id: '2',
                label: (
                  <Typography
                    className="typo-text body3_size"
                    data-testid={`header-bar-log-out`}
                  >
                    {t('arrTopNav.menuItem.logOut')}
                  </Typography>
                ),
                onClick: () => {
                  SSOLogout();
                },
              },
            ]}
          />
        </div>

        <ArrModal
          showModal={showArrCentralModal}
          titleStartIcon={<OpenBook />}
          onClose={() => setShowArrCentralModal(false)}
          title={t('arrTopNav.arrCentralModal.title')}
          description={t('arrTopNav.arrCentralModal.description')}
          className="arr-central-modal"
          dataTestId="arr-central-modal"
          buttons={[
            {
              id: '1',
              label: t('arrTopNav.arrCentralModal.cancelBtn'),
              onClick: () => setShowArrCentralModal(false),
              variant: 'secondary',
              dataTestId: 'acm-cancel-btn',
            },
            {
              id: '2',
              label: t('arrTopNav.arrCentralModal.exploreNowButton'),
              onClick: () => {
                setShowArrCentralModal(false);
                handleExploreNowClick();
              },
              variant: 'primary',
              dataTestId: 'acm-explorenow-btn',
            },
          ]}
        />

        <NotificationsMenu
          notificationsList={notificationsData || []}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        />
      </Grid>
      <ArrLoadingSpinner overlay isOpen={isChangeUserRolePending} />
    </>
  );
};

export default HeaderBar;
